const initializedSections = new WeakMap();
const gradientStates = new WeakMap();
const svgNamespace = 'http://www.w3.org/2000/svg';
const reducedMotionMediaQuery = '(prefers-reduced-motion: reduce)';
const touchPointerMediaQuery = '(hover: none), (pointer: coarse)';
const gradientVisibleRadius = 160;
const gradientTextureOffset = 28;
const gradientAnglePattern = /^linear-gradient\(\s*(-?(?:\d+|\d*\.\d+))deg\s*,/i;
const gradientAngleAmplitude = 95;
const gradientAngleCycleDuration = 7600;
const gradientTransitionDuration = 0.9;
const gradientAmbientIntroDuration = 900;
const gradientFillClass = 'integrations-sec__gradient-fill';
const gradientFillCurrentClass = 'integrations-sec__gradient-fill-current';
const gradientFillNextClass = 'integrations-sec__gradient-fill-next';
const gradientActiveItemClass = 'is-integrations-gradient-active';
const gradientTransitionClass = 'is-integrations-gradient-transitioning';
const nestedInteractiveSelector =
  'a, button, input, select, textarea, label, [role="button"], [contenteditable="true"]';

const getGsap = () => globalThis.gsap;

const isReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.(reducedMotionMediaQuery).matches;

const getSections = (scope) => {
  if (!scope?.querySelectorAll) {
    return [];
  }

  if (scope.matches?.('.integrations-sec')) {
    return [scope];
  }

  return Array.from(scope.querySelectorAll('.integrations-sec'));
};

const getItems = (section) =>
  Array.from(section.querySelectorAll('.integrations-sec__item'));

const getItemBg = (item) =>
  item?.querySelector?.('.integrations-sec__item-bg') || null;

const getItemFillGradient = (item) => {
  const styles = getComputedStyle(item);

  return (
    styles.getPropertyValue('--integrations-item-fill').trim() ||
    styles.getPropertyValue('--integrations-item-border').trim() ||
    'linear-gradient(270deg, #E5127B 0%, #FF3E07 100%)'
  );
};

const getGradientAngle = (gradient) => {
  const match = gradient?.match(gradientAnglePattern);
  const angle = match ? Number(match[1]) : Number.NaN;

  return Number.isFinite(angle) ? angle : null;
};

const getGradientWithAngle = (gradient, angle) => {
  if (!Number.isFinite(angle)) {
    return gradient;
  }

  return gradient.replace(
    gradientAnglePattern,
    `linear-gradient(${angle.toFixed(3)}deg,`
  );
};

const createGradientLayer = (className) => {
  const layer = document.createElement('div');
  layer.classList.add(className);

  return layer;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const setLayerOpacity = (layer, opacity) => {
  layer.style.opacity = String(opacity);
};

const setGradientProgress = (state, progress) => {
  state.progress = clamp(progress, 0, 1);
  state.fill.style.setProperty(
    '--gradient-radius',
    `${state.progress * gradientVisibleRadius}%`
  );
};

const setGradientOrigin = (state, origin) => {
  state.originX = clamp(origin.x, 0, 100);
  state.originY = clamp(origin.y, 0, 100);
  state.fill.style.setProperty('--gradient-origin-x', `${state.originX}%`);
  state.fill.style.setProperty('--gradient-origin-y', `${state.originY}%`);
};

const setGradientTexturePosition = (state, position) => {
  state.textureX = clamp(position.x, 0, 100);
  state.textureY = clamp(position.y, 0, 100);
  state.fill.style.setProperty('--gradient-texture-x', `${state.textureX}%`);
  state.fill.style.setProperty('--gradient-texture-y', `${state.textureY}%`);
};

const setLayerGradient = (layer, gradient) => {
  layer.style.backgroundImage = gradient;
};

const ensureItemGradientState = (item) => {
  const existingState = gradientStates.get(item);

  if (existingState?.bg?.isConnected) {
    existingState.gradient = getItemFillGradient(item);
    return existingState;
  }

  const bg = getItemBg(item);

  if (!bg) {
    return null;
  }

  let fill = Array.from(bg.children).find((child) =>
    child.classList?.contains(gradientFillClass)
  );

  if (!fill) {
    fill = document.createElement('div');
    fill.classList.add(gradientFillClass);
    bg.appendChild(fill);
  }

  let currentLayer = fill.querySelector(`.${gradientFillCurrentClass}`);
  let nextLayer = fill.querySelector(`.${gradientFillNextClass}`);

  if (!currentLayer) {
    currentLayer = createGradientLayer(gradientFillCurrentClass);
    fill.prepend(currentLayer);
  }

  if (!nextLayer) {
    nextLayer = createGradientLayer(gradientFillNextClass);
    fill.appendChild(nextLayer);
  }

  const gradient = getItemFillGradient(item);
  const state = {
    item,
    bg,
    fill,
    currentLayer,
    nextLayer,
    gradient,
    progress: 0,
    originX: 50,
    originY: 50,
    textureX: 50,
    textureY: 50,
    timeline: null,
  };

  setLayerGradient(currentLayer, gradient);
  setLayerGradient(nextLayer, gradient);
  setLayerOpacity(currentLayer, 1);
  setLayerOpacity(nextLayer, 0);
  setGradientOrigin(state, { x: 50, y: 50 });
  setGradientTexturePosition(state, { x: 50, y: 50 });
  setGradientProgress(state, 0);
  gradientStates.set(item, state);

  return state;
};

const getLayerOpacity = (layer) => {
  const gsapInstance = getGsap();
  const opacity = gsapInstance
    ? Number(gsapInstance.getProperty(layer, 'opacity'))
    : Number(getComputedStyle(layer).opacity);

  return Number.isFinite(opacity) ? opacity : 1;
};

const getVisibleLayerBackground = (state) =>
  getLayerOpacity(state.nextLayer) > getLayerOpacity(state.currentLayer)
    ? state.nextLayer.style.backgroundImage
    : state.currentLayer.style.backgroundImage;

const getRectBoundaryOrigin = (rect, point) => {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const directionX = point.x - centerX;
  const directionY = point.y - centerY;

  if ((!directionX && !directionY) || !rect.width || !rect.height) {
    return { x: 50, y: 50 };
  }

  const horizontalScale = directionX
    ? rect.width / 2 / Math.abs(directionX)
    : Number.POSITIVE_INFINITY;
  const verticalScale = directionY
    ? rect.height / 2 / Math.abs(directionY)
    : Number.POSITIVE_INFINITY;
  const scale = Math.min(horizontalScale, verticalScale);
  const boundaryX = centerX + directionX * scale;
  const boundaryY = centerY + directionY * scale;

  return {
    x: ((boundaryX - rect.left) / rect.width) * 100,
    y: ((boundaryY - rect.top) / rect.height) * 100,
  };
};

const getDirectionalOrigins = (sourceItem, targetItem) => {
  const sourceRect = sourceItem.getBoundingClientRect();
  const targetRect = targetItem.getBoundingClientRect();
  const sourceCenter = {
    x: sourceRect.left + sourceRect.width / 2,
    y: sourceRect.top + sourceRect.height / 2,
  };
  const targetCenter = {
    x: targetRect.left + targetRect.width / 2,
    y: targetRect.top + targetRect.height / 2,
  };

  return {
    source: getRectBoundaryOrigin(sourceRect, targetCenter),
    target: getRectBoundaryOrigin(targetRect, sourceCenter),
    hasSize:
      Boolean(sourceRect.width) &&
      Boolean(sourceRect.height) &&
      Boolean(targetRect.width) &&
      Boolean(targetRect.height),
  };
};

const getPointerTexturePosition = (item, event) => {
  const rect = item.getBoundingClientRect();

  if (
    !rect.width ||
    !rect.height ||
    !Number.isFinite(event.clientX) ||
    !Number.isFinite(event.clientY)
  ) {
    return { x: 50, y: 50 };
  }

  const positionX = clamp((event.clientX - rect.left) / rect.width, 0, 1);
  const positionY = clamp((event.clientY - rect.top) / rect.height, 0, 1);

  return {
    x: 50 + (positionX - 0.5) * gradientTextureOffset * 2,
    y: 50 + (positionY - 0.5) * gradientTextureOffset * 2,
  };
};

const setActiveGradientItem = (items, activeItem) => {
  items.forEach((item) => {
    item.classList.toggle(gradientActiveItemClass, item === activeItem);
  });
};

const setupGradientHover = (
  section,
  container,
  items,
  centerItem
) => {
  const states = items.map(ensureItemGradientState).filter(Boolean);
  const centerState = gradientStates.get(centerItem);

  if (!centerState || states.length !== items.length) {
    return null;
  }

  let activeItem = centerItem;
  let requestedItem = centerItem;
  let hoverTimeline = null;
  let focusFrame = null;
  let activeGradientStartedAt = null;
  const touchPointerMedia = window.matchMedia?.(touchPointerMediaQuery);
  const isTouchPointer = () => Boolean(touchPointerMedia?.matches);

  section.classList.add('is-integrations-gradient-ready');
  setActiveGradientItem(items, centerItem);

  const clearTransitionState = () => {
    states.forEach((state) => {
      state.timeline = null;
      state.fill.classList.remove(gradientTransitionClass);
    });
  };

  const getMostVisibleState = () => {
    const fallbackState = gradientStates.get(activeItem) || centerState;

    return states.reduce(
      (mostVisible, state) =>
        state.progress > mostVisible.progress ? state : mostVisible,
      fallbackState
    );
  };

  const settleGradientState = (targetItem) => {
    const targetState = gradientStates.get(targetItem) || centerState;

    states.forEach((state) => {
      const isTarget = state === targetState;

      setGradientProgress(state, isTarget ? 1 : 0);
      setGradientOrigin(state, { x: 50, y: 50 });
      setGradientTexturePosition(state, { x: 50, y: 50 });
      setLayerOpacity(state.currentLayer, 1);
      setLayerOpacity(state.nextLayer, 0);
      setLayerGradient(state.currentLayer, state.gradient);
      setLayerGradient(state.nextLayer, state.gradient);
      state.fill.classList.remove(gradientTransitionClass);
      state.timeline = null;
    });

    activeItem = targetState.item;
    requestedItem = targetState.item;
    activeGradientStartedAt = null;
    setActiveGradientItem(items, targetState.item);
  };

  const animateAmbientGradient = (time) => {
    const activeState = gradientStates.get(activeItem);

    if (
      activeState &&
      activeState.progress >= 0.999 &&
      !activeState.timeline &&
      !isReducedMotion()
    ) {
      const baseAngle = getGradientAngle(activeState.gradient);

      if (baseAngle !== null) {
        if (activeGradientStartedAt === null) {
          activeGradientStartedAt = time;
        }

        const elapsed = Math.max(0, time - activeGradientStartedAt);
        const phase = (elapsed % gradientAngleCycleDuration) / gradientAngleCycleDuration;
        const introProgress = clamp(
          elapsed / gradientAmbientIntroDuration,
          0,
          1
        );
        const angle =
          baseAngle +
          Math.sin(phase * Math.PI * 2) * gradientAngleAmplitude * introProgress;

        setLayerGradient(
          activeState.currentLayer,
          getGradientWithAngle(activeState.gradient, angle)
        );
      }
    }

    window.requestAnimationFrame(animateAmbientGradient);
  };

  const killGradientTimeline = () => {
    hoverTimeline?.kill();
    hoverTimeline = null;
    clearTransitionState();
  };

  const animateGradientOrigin = (timeline, state, origin) => {
    const targetOrigin = {
      x: clamp(origin.x, 0, 100),
      y: clamp(origin.y, 0, 100),
    };

    if (state.progress <= 0.001 || state.progress >= 0.999) {
      setGradientOrigin(state, targetOrigin);
      return;
    }

    timeline.to(
      state,
      {
        originX: targetOrigin.x,
        originY: targetOrigin.y,
        duration: 0.22,
        ease: 'power2.out',
        onUpdate: () =>
          setGradientOrigin(state, { x: state.originX, y: state.originY }),
      },
      0
    );
  };

  const transitionGradient = (targetItem, { immediate = false } = {}) => {
    const targetState = gradientStates.get(targetItem);

    if (!targetState) {
      return;
    }

    if (hoverTimeline && requestedItem === targetItem) {
      return;
    }

    const sourceState = getMostVisibleState();
    const origins = getDirectionalOrigins(sourceState.item, targetItem);
    const gsapInstance = getGsap();
    const hasOtherVisibleState = states.some(
      (state) => state !== targetState && state.progress > 0.001
    );

    if (
      !hoverTimeline &&
      sourceState === targetState &&
      targetState.progress >= 0.999 &&
      !hasOtherVisibleState
    ) {
      settleGradientState(targetItem);
      return;
    }

    const incomingGradient =
      getVisibleLayerBackground(sourceState) || sourceState.gradient;

    killGradientTimeline();
    requestedItem = targetItem;

    if (immediate || isReducedMotion() || !gsapInstance || !origins.hasSize) {
      settleGradientState(targetItem);
      return;
    }

    setLayerGradient(targetState.currentLayer, targetState.gradient);
    setLayerGradient(targetState.nextLayer, incomingGradient);
    setGradientTexturePosition(targetState, {
      x: sourceState.textureX,
      y: sourceState.textureY,
    });
    setLayerOpacity(targetState.currentLayer, 0);
    setLayerOpacity(targetState.nextLayer, 1);

    setActiveGradientItem(items, targetItem);

    const outgoingStates = states.filter(
      (state) => state !== targetState && state.progress > 0.001
    );
    const transitioningStates = new Set([...outgoingStates, targetState]);

    transitioningStates.forEach((state) => {
      state.fill.classList.add(gradientTransitionClass);
    });

    hoverTimeline = gsapInstance.timeline({
      defaults: {
        overwrite: 'auto',
      },
      onComplete: () => {
        hoverTimeline = null;
        settleGradientState(targetItem);
      },
    });
    transitioningStates.forEach((state) => {
      state.timeline = hoverTimeline;
    });

    animateGradientOrigin(hoverTimeline, targetState, origins.target);
    outgoingStates.forEach((state) => {
      const stateOrigins =
        state === sourceState
          ? origins
          : getDirectionalOrigins(state.item, targetItem);

      animateGradientOrigin(hoverTimeline, state, stateOrigins.source);
    });

    outgoingStates.forEach((state) => {
      const baseDuration = gradientTransitionDuration;
      const duration = baseDuration * Math.max(0.25, state.progress);

      hoverTimeline.to(
        state,
        {
          progress: 0,
          duration,
          ease: 'power3.inOut',
          onUpdate: () => setGradientProgress(state, state.progress),
        },
        0
      );
    });

    hoverTimeline.to(
      targetState,
      {
        progress: 1,
        duration: gradientTransitionDuration * Math.max(0.25, 1 - targetState.progress),
        ease: 'power3.inOut',
        onUpdate: () => setGradientProgress(targetState, targetState.progress),
      },
      0
    );
    hoverTimeline.to(
      targetState.nextLayer,
      {
        opacity: 0,
        duration: gradientTransitionDuration,
        ease: 'power2.inOut',
      },
      0
    );
    hoverTimeline.to(
      targetState.currentLayer,
      {
        opacity: 1,
        duration: gradientTransitionDuration,
        ease: 'power2.inOut',
      },
      0
    );
  };

  settleGradientState(centerItem);
  window.requestAnimationFrame(animateAmbientGradient);

  const handlePointerOver = (event) => {
    if (isTouchPointer() || !event.target?.closest) {
      return;
    }

    const item = event.target.closest('.integrations-sec__item');

    if (!item || !container.contains(item)) {
      return;
    }

    if (event.relatedTarget && item.contains(event.relatedTarget)) {
      return;
    }

    transitionGradient(item);
  };

  const handlePointerMove = (event) => {
    if (
      event.pointerType === 'touch' ||
      isReducedMotion() ||
      hoverTimeline ||
      !event.target?.closest
    ) {
      return;
    }

    const item = event.target.closest('.integrations-sec__item');

    if (!item || !container.contains(item) || item !== activeItem) {
      return;
    }

    const state = gradientStates.get(item);

    if (!state || state.progress < 0.999) {
      return;
    }

    setGradientTexturePosition(state, getPointerTexturePosition(item, event));
  };

  container.addEventListener('pointerover', handlePointerOver);
  container.addEventListener('pointermove', handlePointerMove);

  if (typeof PointerEvent === 'undefined') {
    container.addEventListener('mouseover', handlePointerOver);
  }

  container.addEventListener('pointerleave', () => {
    if (!isTouchPointer()) {
      transitionGradient(centerItem);
    }
  });
  container.addEventListener('click', (event) => {
    if (!isTouchPointer() || !event.target?.closest) {
      return;
    }

    const item = event.target.closest('.integrations-sec__item');
    const interactiveElement = event.target.closest(nestedInteractiveSelector);

    if (
      !item ||
      !container.contains(item) ||
      (interactiveElement && item.contains(interactiveElement))
    ) {
      return;
    }

    transitionGradient(item);
  });
  container.addEventListener('focusin', (event) => {
    const item = event.target?.closest?.('.integrations-sec__item');

    if (item && container.contains(item)) {
      transitionGradient(item);
    }
  });
  container.addEventListener('focusout', () => {
    if (focusFrame !== null) {
      window.cancelAnimationFrame(focusFrame);
    }

    focusFrame = window.requestAnimationFrame(() => {
      focusFrame = null;

      if (!isTouchPointer() && !container.contains(document.activeElement)) {
        transitionGradient(centerItem);
      }
    });
  });

  return {
    refresh() {
      const mostVisibleState = getMostVisibleState();

      killGradientTimeline();
      settleGradientState(mostVisibleState.item);
    },
  };
};

const getOrCreateLinesLayer = (container) => {
  const existingLayer = Array.from(container.children).find((child) =>
    child.classList?.contains('integrations-sec__lines')
  );

  if (existingLayer) {
    return existingLayer;
  }

  const layer = document.createElementNS(svgNamespace, 'svg');
  layer.classList.add('integrations-sec__lines');
  layer.setAttribute('aria-hidden', 'true');
  layer.setAttribute('focusable', 'false');
  layer.setAttribute('preserveAspectRatio', 'none');
  container.appendChild(layer);

  return layer;
};

const createLine = (layer, targetItem) => {
  const line = document.createElementNS(svgNamespace, 'line');
  line.classList.add('integrations-sec__line');
  line.style.stroke =
    getComputedStyle(targetItem)
      .getPropertyValue('--integrations-item-line-color')
      .trim() || '#FF3E07';
  layer.appendChild(line);

  return line;
};

const getRelativeRect = (element, parentRect) => {
  const rect = element.getBoundingClientRect();

  return {
    left: rect.left - parentRect.left,
    top: rect.top - parentRect.top,
    right: rect.right - parentRect.left,
    bottom: rect.bottom - parentRect.top,
    width: rect.width,
    height: rect.height,
  };
};

const getRectCenter = (rect) => ({
  x: rect.left + rect.width / 2,
  y: rect.top + rect.height / 2,
});

const getAnchorPoint = (rect, directionX, directionY) => {
  const absX = Math.abs(directionX);
  const absY = Math.abs(directionY);

  if (absX > 1 && absY > 1) {
    return {
      x: directionX > 0 ? rect.right : rect.left,
      y: directionY > 0 ? rect.bottom : rect.top,
    };
  }

  if (absX >= absY) {
    return {
      x: directionX > 0 ? rect.right : rect.left,
      y: rect.top + rect.height / 2,
    };
  }

  return {
    x: rect.left + rect.width / 2,
    y: directionY > 0 ? rect.bottom : rect.top,
  };
};

const setLinePoint = (line, attribute, value) => {
  line.setAttribute(attribute, String(Math.round(value * 100) / 100));
};

const initIntegrationsSection = (scope = document) => {
  getSections(scope).forEach((section) => {
    const existingInstance = initializedSections.get(section);

    if (existingInstance) {
      existingInstance.requestUpdate();
      existingInstance.gradientHover?.refresh();
      return;
    }

    const container = section.querySelector('.integrations-sec__container');
    const items = getItems(section);
    const centerItem = section.querySelector('.integrations-sec__item--center');

    if (!container || !centerItem || items.length < 2) {
      return;
    }

    const layer = getOrCreateLinesLayer(container);
    const targetItems = items.filter((item) => item !== centerItem);
    layer.replaceChildren();

    const lines = targetItems.map((item) => ({
      item,
      line: createLine(layer, item),
    }));
    let frame = null;
    const gradientHover = setupGradientHover(
      section,
      container,
      items,
      centerItem
    );

    const update = () => {
      const containerRect = container.getBoundingClientRect();

      if (!containerRect.width || !containerRect.height) {
        return;
      }

      const centerRect = getRelativeRect(centerItem, containerRect);
      const centerPoint = getRectCenter(centerRect);

      layer.setAttribute(
        'viewBox',
        `0 0 ${containerRect.width} ${containerRect.height}`
      );

      lines.forEach(({ item, line }) => {
        const itemRect = getRelativeRect(item, containerRect);
        const itemPoint = getRectCenter(itemRect);
        const directionX = itemPoint.x - centerPoint.x;
        const directionY = itemPoint.y - centerPoint.y;
        const sourcePoint = getAnchorPoint(centerRect, directionX, directionY);
        const targetPoint = getAnchorPoint(itemRect, -directionX, -directionY);

        setLinePoint(line, 'x1', sourcePoint.x);
        setLinePoint(line, 'y1', sourcePoint.y);
        setLinePoint(line, 'x2', targetPoint.x);
        setLinePoint(line, 'y2', targetPoint.y);
      });
    };

    const requestUpdate = () => {
      if (frame !== null) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        frame = null;
        update();
      });
    };

    let resizeObserver = null;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(requestUpdate);
      resizeObserver.observe(container);
      items.forEach((item) => resizeObserver.observe(item));
    }

    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('orientationchange', requestUpdate, {
      passive: true,
    });

    const instance = {
      requestUpdate,
      resizeObserver,
      gradientHover,
    };

    initializedSections.set(section, instance);
    requestUpdate();
  });
};

if (!window.__boldIntegrationsSectionRuntime) {
  window.__boldIntegrationsSectionRuntime = true;
  initIntegrationsSection();
  window.initIntegrationsSection = initIntegrationsSection;
}
